import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';

import { meta } from '../../lib/api.js';
import { useHotkeys } from '../../hooks/useHotkeys.js';
import { buildReviewSegments } from '../../lib/derive.js';
import { Keycap, Spinner } from '../common/index.jsx';

/**
 * Finished annotations shown as worked examples, before anyone starts their own.
 *
 * Read-only by construction rather than by discipline: the data comes from a GET that
 * has no writing counterpart, nothing here is an input, and no store is touched. There
 * is no editing path to disable and therefore none to forget.
 *
 * It shows exactly what an annotator sees while working - the agent's final caption and
 * the human's tags - and nothing the annotator view withholds. No pipeline spans, no
 * agent decisions, no model name: an example that revealed those would teach the
 * anchoring bias the annotator view exists to prevent.
 */
export function WorkedExamples({ standalone = false }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [at, setAt] = useState(0);

  const total = items?.length ?? 0;

  const step = useCallback(
    (delta) => setAt((i) => Math.min(Math.max(0, i + delta), Math.max(0, total - 1))),
    [total],
  );

  // Registered before the early returns below - a hook may not be called conditionally.
  // `useHotkeys` already ignores keys typed into inputs and leaves unbound keys alone,
  // so the browser's own arrow behaviour survives everywhere else on the page.
  useHotkeys(
    {
      ArrowLeft: () => step(-1),
      ArrowRight: () => step(1),
      Home: () => setAt(0),
      End: () => setAt(Math.max(0, total - 1)),
    },
    { enabled: total > 1 },
  );

  useEffect(() => {
    meta
      .examples()
      .then((r) => {
        setItems(r.items || []);
        setError('');
      })
      .catch((err) => {
        setItems([]);
        setError(err?.message || 'Could not load the examples.');
      });
  }, []);

  if (items === null) return <Spinner label="Loading examples…" />;

  // Never vanish silently. Rendering nothing on an error is indistinguishable from
  // "there are no examples", which is what makes a missing endpoint so hard to spot.
  if (error) {
    return (
      <section className="worked-examples">
        <div className="banner warn">
          <span className="grow">
            {error} If the server was updated while this page was open, reload it.
          </span>
        </div>
      </section>
    );
  }

  if (!items.length) {
    return standalone ? (
      <div className="empty-state">
        No examples have been published yet. An admin can choose them from finished
        annotations.
      </div>
    ) : null;
  }

  const example = items[Math.min(at, items.length - 1)];

  return (
    <section className="worked-examples">
      <div className="row-between" style={{ marginBottom: 'var(--space-3)' }}>
        <div>
          <h2 className="section-title" style={{ margin: 0 }}>
            Worked examples
          </h2>
          <p className="tiny muted" style={{ margin: 0 }}>
            Finished annotations, for reference. Nothing here can be edited. Use{' '}
            <Keycap>&larr;</Keycap> <Keycap>&rarr;</Keycap> to move between them.
          </p>
        </div>
        <div className="row gap-1">
          <button
            type="button"
            className="btn sm"
            disabled={at === 0}
            onClick={() => step(-1)}
            aria-label="Previous example"
            title="Previous example (←)"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="tiny muted" style={{ minWidth: '4.5em', textAlign: 'center' }}>
            {at + 1} of {items.length}
          </span>
          <button
            type="button"
            className="btn sm"
            disabled={at >= items.length - 1}
            onClick={() => step(1)}
            aria-label="Next example"
            title="Next example (→)"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <ExampleCard example={example} />
    </section>
  );
}

/**
 * The key for the combined caption.
 *
 * Built from styled SAMPLES rather than colour chips, and using the very classes the
 * caption itself renders with, for two reasons: a chip cannot show a strikethrough, an
 * insertion or an italic note, and a key written independently of the renderer drifts
 * from it the first time either changes.
 */
function ReviewLegend() {
  return (
    <div className="legend review-legend">
      <span className="legend-item">
        <mark className="ann-hallucination review-struck">hallucination</mark>
        removed — not in the image
      </span>
      <span className="legend-item">
        <mark className="review-inserted">replacement</mark>
        what it should say instead
      </span>
      <span className="legend-item">
        <mark className="ann-uncertain">uncertain</mark>
        kept
        <mark className="review-note"> &lt;why&gt;</mark>
      </span>
      <span className="legend-item">
        <mark className="ann-wrong review-struck">wrong uncertainty</mark>
        hedged although the image is clear
      </span>
      <span className="legend-item">
        <mark className="review-added">added</mark>
        detail the annotator wrote in
      </span>
    </div>
  );
}

function ExampleCard({ example }) {
  // One caption carrying every edit: strikethrough where text was removed, the
  // replacement inserted after it, the uncertainty note in brackets. Showing the
  // before and after as two captions made the reader diff them by eye.
  const segments = useMemo(
    () =>
      buildReviewSegments(example.working_caption, example.tag_spans || [], {
        // Lets the view mark text the annotator typed, which the tags alone cannot show.
        agentCaption: example.agent_final_caption,
      }),
    [example],
  );

  const untouched =
    !(example.tag_spans || []).length && !(example.added_text_spans || []).length;

  return (
    <div className="example-card">
      <div className="example-image">
        {example.image_missing ? (
          <div className="empty-state">
            <ImageOff size={16} /> image unavailable
          </div>
        ) : (
          <img src={example.image_url} alt="" loading="lazy" />
        )}
      </div>

      <div className="example-body stack">
        {example.teaching_note && <p className="banner info">{example.teaching_note}</p>}

        <div>
          <div className="row-between" style={{ marginBottom: 'var(--space-1)' }}>
            <span className="section-title" style={{ margin: 0 }}>
              The caption, with every edit shown
            </span>
            <span className="tiny muted">
              {example.counts.hallucinated} hallucination
              {example.counts.hallucinated === 1 ? '' : 's'} · {example.counts.uncertain} uncertain
              · {example.counts.wrong_uncertainty} wrong uncertainty ·{' '}
              {example.counts.added_spans} added
            </span>
          </div>
          {/* The key reads before the caption, not after: it is what makes the colours
              mean anything, and underneath it was being found only once you had already
              tried to read past it. */}
          <ReviewLegend />
          <p className="caption-surface caption-body review-caption">
            {segments.map((seg, i) => (
              <mark key={i} className={seg.className || 'review-plain'} title={seg.title}>
                {seg.text}
              </mark>
            ))}
          </p>
          {untouched && (
            <p className="tiny muted">Nothing was tagged in this caption — it read correctly.</p>
          )}
        </div>

        <details className="disclosure">
          <summary>The caption as received, and the final text</summary>
          <div className="stack" style={{ marginTop: 'var(--space-2)' }}>
            <div>
              <span className="section-title">As received</span>
              <p className="caption-surface caption-body">{example.agent_final_caption}</p>
            </div>
            <div>
              <span className="section-title">Final</span>
              <p className="caption-surface caption-body">{example.final_caption}</p>
            </div>
          </div>
        </details>

        {example.notes && (
          <div>
            <span className="section-title">Annotator notes</span>
            <p className="tiny muted">{example.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
